import { fetcher } from '@commenda/utils';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { keyPersonsKeys } from './keys';
import { KEY_PERSONS_ROUTE } from './routes';
import type {
  IAttributeRegistry,
  IGovernanceStructure,
  IIdentityDocument,
  IKeyPersonDeletePreview,
  IKeyPersonDetail,
  IKeyPersonEligibleEntity,
  IKeyPersonRoleAssignmentsResponse,
  IKeyPersonsForEntityResponse,
  IKeyPersonsListResponse,
} from '@commenda/types';
import type { KeyPersonsListParams } from './types';

export type {
  IAttributeRegistry,
  IGovernanceStructure,
  IIdentityDocument,
  IKeyPersonDeletePreview,
  IKeyPersonDetail,
  IKeyPersonEligibleEntity,
  IKeyPersonRoleAssignmentsResponse,
  IKeyPersonsForEntityResponse,
  IKeyPersonsListResponse,
};

const fetchKeyPersons = async (
  params: KeyPersonsListParams
): Promise<IKeyPersonsListResponse> => {
  const query = new URLSearchParams({ companyId: String(params.companyId) });
  if (params.search) query.set('search', params.search);
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  if (params.status) query.set('status', params.status);
  if (params.corporationId !== undefined) query.set('corporationId', String(params.corporationId));
  if (params.roleType) query.set('roleType', params.roleType);
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortOrder) query.set('sortOrder', params.sortOrder);

  const res = await fetcher(`${KEY_PERSONS_ROUTE}?${query.toString()}`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error('Failed to fetch key persons');
  return (await res.json()) as IKeyPersonsListResponse;
};

export function useGetKeyPersons(
  companyId: number | null | undefined,
  options?: Omit<KeyPersonsListParams, 'companyId'>
) {
  const params: KeyPersonsListParams = {
    companyId: companyId as number,
    ...options,
  };
  return useQuery({
    queryKey: keyPersonsKeys.list(params),
    queryFn: () => fetchKeyPersons(params),
    enabled: companyId != null && companyId > 0,
    // Keep the previous page rendered mid-request so the table doesn't flash empty.
    placeholderData: keepPreviousData,
  });
}

const fetchKeyPersonsForEntity = async (
  corporationId: number
): Promise<IKeyPersonsForEntityResponse> => {
  const res = await fetcher(`business-entities/${corporationId}/key-persons`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error('Failed to fetch key persons for entity');
  return (await res.json()) as IKeyPersonsForEntityResponse;
};

/** Key persons holding a role at a single entity, with their roles AT that entity. */
export function useGetKeyPersonsForEntity(
  corporationId: number | null | undefined
) {
  return useQuery({
    queryKey: keyPersonsKeys.forEntity(corporationId ?? 0),
    queryFn: () => fetchKeyPersonsForEntity(corporationId as number),
    enabled: corporationId != null && corporationId > 0,
    placeholderData: keepPreviousData,
  });
}

const fetchDeletePreview = async (
  id: number
): Promise<IKeyPersonDeletePreview> => {
  const res = await fetcher(`${KEY_PERSONS_ROUTE}/${id}/delete-preview`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error('Failed to fetch delete preview');
  return (await res.json()) as IKeyPersonDeletePreview;
};

export function useGetDeletePreview(
  id: number | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: keyPersonsKeys.deletePreview(id as number),
    queryFn: () => fetchDeletePreview(id as number),
    enabled: options?.enabled !== false && !!id,
  });
}

const fetchKeyPerson = async (id: number): Promise<IKeyPersonDetail> => {
  const res = await fetcher(`${KEY_PERSONS_ROUTE}/${id}`, { method: 'GET' });
  if (!res.ok) throw new Error('Failed to fetch key person');
  return (await res.json()) as IKeyPersonDetail;
};

export function useGetKeyPerson(id: number | null | undefined) {
  return useQuery({
    queryKey: keyPersonsKeys.detail(id as number),
    queryFn: () => fetchKeyPerson(id as number),
    enabled: !!id,
  });
}

const fetchRoleAssignments = async (
  id: number
): Promise<IKeyPersonRoleAssignmentsResponse> => {
  const res = await fetcher(`${KEY_PERSONS_ROUTE}/${id}/role-assignments`, { method: 'GET' });
  if (!res.ok) throw new Error('Failed to fetch role assignments');
  return (await res.json()) as IKeyPersonRoleAssignmentsResponse;
};

export function useGetRoleAssignments(id: number | null | undefined) {
  return useQuery({
    queryKey: keyPersonsKeys.roleAssignments(id as number),
    queryFn: () => fetchRoleAssignments(id as number),
    enabled: !!id,
  });
}

const fetchRoleEligibleEntities = async (
  id: number
): Promise<IKeyPersonEligibleEntity[]> => {
  const res = await fetcher(`${KEY_PERSONS_ROUTE}/${id}/role-eligible-entities`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error('Failed to fetch eligible entities');
  return (await res.json()) as IKeyPersonEligibleEntity[];
};

export function useGetRoleEligibleEntities(
  id: number | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: keyPersonsKeys.roleEligibleEntities(id as number),
    queryFn: () => fetchRoleEligibleEntities(id as number),
    enabled: options?.enabled !== false && !!id,
  });
}

const fetchIdentityDocuments = async (
  id: number,
  country: string
): Promise<IIdentityDocument[]> => {
  const query = new URLSearchParams({ country });
  const res = await fetcher(
    `${KEY_PERSONS_ROUTE}/${id}/identity-documents?${query.toString()}`,
    { method: 'GET' }
  );
  if (!res.ok) throw new Error('Failed to fetch identity documents');
  return (await res.json()) as IIdentityDocument[];
};

export function useGetIdentityDocuments(
  id: number | null | undefined,
  country: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: keyPersonsKeys.identityDocuments(id as number, country as string),
    queryFn: () => fetchIdentityDocuments(id as number, country as string),
    enabled:
      options?.enabled !== false && !!id && Boolean(country),
  });
}

const fetchAttributeProperties = async (
  companyId: number
): Promise<IAttributeRegistry> => {
  const query = new URLSearchParams({ companyId: String(companyId) });
  const res = await fetcher(`${KEY_PERSONS_ROUTE}/attribute-properties?${query.toString()}`, {
    method: 'GET',
  });
  if (!res.ok) throw new Error('Failed to fetch attribute properties');
  return (await res.json()) as IAttributeRegistry;
};

export function useGetAttributeProperties(
  companyId: number | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: keyPersonsKeys.attributeProperties(companyId as number),
    queryFn: () => fetchAttributeProperties(companyId as number),
    enabled: options?.enabled !== false && companyId != null && companyId > 0,
  });
}
